import AsyncStorage from '@react-native-async-storage/async-storage';
import { MmkvStore } from 'src/data/services/MmkvStorage';

const LEGACY_KEY_MAP: Record<string, string> = {
	events: 'Events2',
	myTimetable: 'MyTimeTableNew',
	myBarTimetable: 'MyBarTimeTable',
	notificationsRead: 'NotificationsRead',
	forms: 'Forms',
	authToken: 'AuthToken',
	news: 'News',
};

function isMigrated(): boolean {
	return MmkvStore.getBool('migrationComplete') === true;
}

async function legacyGet(key: string): Promise<string | null> {
	const legacyKey = LEGACY_KEY_MAP[key] ?? key;
	const value = await AsyncStorage.getItem(legacyKey);
	if (value === 'null' || value === null) return null;
	return value;
}

async function legacyGetObject(key: string): Promise<object | null> {
	const raw = await legacyGet(key);
	if (!raw) return null;
	try {
		return JSON.parse(raw);
	} catch {
		return null;
	}
}

export const Store = {
	set(key: string, value: string | number | boolean | object | null): void {
		MmkvStore.set(key, value);
	},

	save(key: string, value: string | number | boolean | object | null): void {
		MmkvStore.set(key, value);
	},

	// During the migration window (e.g.
	get: async (key: string, def: string | null = null): Promise<string | null> => {
		if (isMigrated()) return MmkvStore.get(key, def);
		const mmkv = MmkvStore.get(key, null);
		if (mmkv !== null) return mmkv;
		const legacy = await legacyGet(key);
		return legacy ?? def;
	},

	getInt: async (key: string, def: number | null = null): Promise<number | null> => {
		if (isMigrated()) return MmkvStore.getInt(key, def);
		const mmkv = MmkvStore.getInt(key, null);
		if (mmkv !== null) return mmkv;
		const legacy = await legacyGet(key);
		if (legacy === null) return def;
		return parseInt(legacy);
	},

	getBool: async (key: string): Promise<boolean | null> => {
		if (isMigrated()) return MmkvStore.getBool(key);
		const mmkv = MmkvStore.getBool(key);
		if (mmkv !== null) return mmkv;
		const legacy = await legacyGet(key);
		if (legacy === null) return null;
		return legacy === 'true';
	},

	getObject: async <T = object>(key: string): Promise<T | null> => {
		if (isMigrated()) return MmkvStore.getObject<T>(key);
		const mmkv = MmkvStore.getObject<T>(key);
		if (mmkv !== null) return mmkv;
		return (await legacyGetObject(key)) as T | null;
	},

	remove(key: string): void {
		MmkvStore.remove(key);
		// Also drop the legacy copy pre-migration, so a logout/clear cannot resurface old data through the legacy
		// fallback above.
		if (!isMigrated()) {
			AsyncStorage.removeItem(LEGACY_KEY_MAP[key] ?? key).catch(() => {});
		}
	},
};
